/**
 * Drawing canvas refs management
 *
 * Extracted from drawing.store.ts for better modularity.
 * Manages DOM refs outside of Zustand store to avoid serialization issues.
 *
 * Using Map instead of WeakMap since string keys are not WeakKey compatible.
 * Refs are keyed by session ID for multi-instance support.
 */

// Canvas and context refs storage
const canvasRefs = new Map<string, HTMLCanvasElement>();
const contextRefs = new Map<string, CanvasRenderingContext2D>();

// Current active session tracking
let currentSessionId: string | null = null;

// Track initialized canvas to prevent re-initialization
let initializedCanvasKey: string | null = null;

/**
 * Get current session ID
 */
export const getCurrentSessionId = (): string | null => currentSessionId;

/**
 * Set current session ID
 */
export const setCurrentSessionId = (id: string | null): void => {
  currentSessionId = id;
};

/**
 * Get initialized canvas key
 */
export const getInitializedCanvasKey = (): string | null =>
  initializedCanvasKey;

/**
 * Set initialized canvas key
 */
export const setInitializedCanvasKey = (key: string | null): void => {
  initializedCanvasKey = key;
};

/**
 * Store canvas ref for a session
 */
export const setCanvasRef = (
  sessionId: string,
  canvas: HTMLCanvasElement
): void => {
  canvasRefs.set(sessionId, canvas);
};

/**
 * Get canvas ref for current session or specified session
 */
export const getCanvasRef = (sessionId?: string): HTMLCanvasElement | null => {
  const id = sessionId ?? currentSessionId;
  return id ? (canvasRefs.get(id) ?? null) : null;
};

/**
 * Store context ref for a session
 */
export const setContextRef = (
  sessionId: string,
  context: CanvasRenderingContext2D
): void => {
  contextRefs.set(sessionId, context);
};

/**
 * Get context ref for current session or specified session
 */
export const getContextRef = (
  sessionId?: string
): CanvasRenderingContext2D | null => {
  const id = sessionId ?? currentSessionId;
  return id ? (contextRefs.get(id) ?? null) : null;
};

/**
 * Clean up refs for a session
 */
export const cleanupSessionRefs = (sessionId: string): void => {
  canvasRefs.delete(sessionId);
  contextRefs.delete(sessionId);
};

/**
 * Reset all refs (used on session end)
 */
export const resetAllRefs = (): void => {
  if (currentSessionId) {
    cleanupSessionRefs(currentSessionId);
  }
  currentSessionId = null;
  initializedCanvasKey = null;
};
